import React, { useState, useEffect, useCallback } from 'react';
import { getPlanHistory, setActivePlan, deletePlan, savePlan, duplicatePlan, renamePlan } from '../services/api';
import LessonPlanPreview from './LessonPlanPreview';
import '../styles/PlanHistoryPanel.css';

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function planLabel(plan) {
  if (plan.plan_type === 'testing') return 'Assessment';
  if (plan.plan_type === 'cycles') return 'Reading Cycles';
  return plan.plan_type;
}

function planSummary(plan) {
  const pj = plan.plan_json || {};
  if (plan.plan_type === 'testing') {
    const n = Array.isArray(pj.questions) ? pj.questions.length : '?';
    return `${n} question${n !== 1 ? 's' : ''}`;
  }
  if (plan.plan_type === 'cycles') {
    const n = Array.isArray(pj.cycles) ? pj.cycles.length : '?';
    return `${n} cycle${n !== 1 ? 's' : ''}`;
  }
  return '';
}

// ── Preview/Edit modal ────────────────────────────────────────────────────────

function PlanModal({ plan, paperName, user, paper, onClose, onSaved }) {
  const [editedPlan, setEditedPlan] = useState(plan.plan_json);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleEditSection = (index, updatedSection) => {
    const isTest = editedPlan.plan_type === 'testing';
    const key = isTest ? 'questions' : 'cycles';
    setEditedPlan(prev => ({ ...prev, [key]: prev[key].map((s, i) => i === index ? updatedSection : s) }));
    setSaved(false);
  };

  const handleReorderSection = (fromIndex, toIndex) => {
    setEditedPlan(prev => {
      const questions = [...prev.questions];
      const [moved] = questions.splice(fromIndex, 1);
      questions.splice(toIndex, 0, moved);
      // Update question numbers to reflect new order
      const renumbered = questions.map((q, i) => ({ ...q, number: i + 1 }));
      return { ...prev, questions: renumbered };
    });
    setSaved(false);
  };

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      await savePlan(user.id, paper.id, editedPlan, null, '', plan.id);
      setSaved(true);
      onSaved?.();
    } catch {
      alert('Failed to save edits.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="php-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="php-modal">
        <div className="php-modal-header">
          <span className="php-modal-title">{planLabel(plan)} · {formatDate(plan.created_at)}</span>
          <div className="php-modal-header-actions">
            <button
              className={`php-drawer-save${saved ? ' php-drawer-save--saved' : ''}`}
              onClick={handleSaveEdits}
              disabled={saving}
            >
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save edits'}
            </button>
            <button className="php-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>
        <div className="php-modal-body">
          <LessonPlanPreview
            plan={editedPlan}
            paperName={paperName}
            onEditSection={handleEditSection}
            onReorderSection={handleReorderSection}
            readOnly={false}
            onApprove={() => {}}
            onRequestChanges={() => {}}
            approvalNotes=""
            onApprovalNotesChange={() => {}}
            hideApproval
          />
          {editedPlan.plan_type === 'testing' && editedPlan.is_bank && (
            <div className="php-bank-section">
              <div className="php-bank-row">
                <span className="php-bank-label">Questions per student</span>
                <input
                  type="number"
                  min={1}
                  max={Array.isArray(editedPlan.questions) ? editedPlan.questions.length : 100}
                  value={editedPlan.sample_size ?? 5}
                  className="php-bank-input"
                  onChange={e => {
                    const total = Array.isArray(editedPlan.questions) ? editedPlan.questions.length : 100;
                    const val = Math.min(Math.max(1, parseInt(e.target.value, 10) || 1), total);
                    setEditedPlan(prev => ({ ...prev, sample_size: val }));
                    setSaved(false);
                  }}
                />
                <span className="php-bank-of">of {Array.isArray(editedPlan.questions) ? editedPlan.questions.length : '?'} total</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function PlanHistoryPanel({ paper, user, onClose, onPlanActivated }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actioning, setActioning] = useState(null);
  const [previewPlan, setPreviewPlan] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const loadHistory = useCallback(async () => {
    if (!paper?.id || !user?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await getPlanHistory(paper.id, user.id);
      setPlans(data.plans || []);
    } catch {
      setError('Failed to load plan history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [paper?.id, user?.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleSetActive = async (planId) => {
    setActioning(planId);
    try {
      await setActivePlan(planId, user.id, paper.id);
      setPlans(prev => {
        const planType = prev.find(p => p.id === planId)?.plan_type;
        onPlanActivated?.(planType);
        return prev.map(p => ({ ...p, is_active: p.plan_type === planType ? p.id === planId : p.is_active }));
      });
    } catch {
      alert('Failed to set active plan.');
    } finally {
      setActioning(null);
    }
  };

  const handleDuplicate = async (planId) => {
    setActioning(planId);
    try {
      const newPlan = await duplicatePlan(planId, user.id);
      setPlans(prev => [newPlan, ...prev]);
    } catch {
      alert('Failed to duplicate plan.');
    } finally {
      setActioning(null);
    }
  };

  const handleRenameSubmit = async (planId) => {
    try {
      await renamePlan(planId, user.id, renameValue);
      setPlans(prev => prev.map(p => p.id === planId ? { ...p, name: renameValue } : p));
    } catch {
      alert('Failed to rename plan.');
    } finally {
      setRenamingId(null);
      setRenameValue('');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    setActioning(planId);
    try {
      await deletePlan(planId, user.id);
      setPlans(prev => prev.filter(p => p.id !== planId));
    } catch {
      alert('Failed to delete plan.');
    } finally {
      setActioning(null);
    }
  };

  const testingPlans = plans.filter(p => p.plan_type === 'testing');
  const cyclePlans = plans.filter(p => p.plan_type === 'cycles');

  function renderGroup(label, group) {
    if (group.length === 0) return null;
    return (
      <div className="php-group">
        <div className="php-group-label">{label}</div>
        {group.map(plan => (
          <div key={plan.id} className={`php-row${plan.is_active ? ' php-row--active' : ''}`}>
            <div className="php-row-meta">
              <div className="php-row-left">
                <span className="php-type-badge">{planLabel(plan)}</span>
                <span className="php-summary">{planSummary(plan)}</span>
                <span className="php-date">{formatDate(plan.created_at)}</span>
                {plan.is_active && <span className="php-active-badge">Active</span>}
              </div>
              {renamingId === plan.id ? (
                <div className="php-rename-row">
                  <input
                    className="php-rename-input"
                    autoFocus
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRenameSubmit(plan.id);
                      if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                    }}
                    placeholder="Enter a name…"
                  />
                  <button className="php-btn php-btn--activate" onClick={() => handleRenameSubmit(plan.id)}>Save</button>
                  <button className="php-btn" onClick={() => { setRenamingId(null); setRenameValue(''); }}>Cancel</button>
                </div>
              ) : (
                <div className="php-plan-name" onClick={() => { setRenamingId(plan.id); setRenameValue(plan.name || ''); }}>
                  {plan.name ? <span className="php-name-text">{plan.name}</span> : <span className="php-name-placeholder">+ Add name</span>}
                </div>
              )}
              <div className="php-row-actions">
                <button
                  className="php-btn php-btn--preview"
                  onClick={() => setPreviewPlan(plan)}
                >
                  Preview &amp; Edit
                </button>
                <button
                  className="php-btn php-btn--duplicate"
                  disabled={actioning === plan.id}
                  onClick={() => handleDuplicate(plan.id)}
                >
                  {actioning === plan.id ? '…' : 'Duplicate'}
                </button>
                {!plan.is_active && (
                  <button
                    className="php-btn php-btn--activate"
                    disabled={actioning === plan.id}
                    onClick={() => handleSetActive(plan.id)}
                  >
                    Set Active
                  </button>
                )}
                <button
                  className="php-btn php-btn--delete"
                  disabled={actioning === plan.id}
                  onClick={() => handleDelete(plan.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="php-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="php-panel">

        <div className="php-header">
          <span className="php-title">Plan History</span>
          <button className="php-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="php-body">
          {loading && <p className="php-loading">Loading…</p>}
          {error && <p className="php-error">{error}</p>}
          {!loading && !error && plans.length === 0 && (
            <p className="php-empty">No saved plans yet.</p>
          )}
          {!loading && !error && (
            <>
              {renderGroup('Assessment Plans', testingPlans)}
              {renderGroup('Reading Cycle Plans', cyclePlans)}
            </>
          )}
        </div>

        {/* Modal rendered outside the panel so it covers the full screen */}
        {previewPlan && (
          <PlanModal
            plan={previewPlan}
            paperName={paper?.name}
            user={user}
            paper={paper}
            onClose={() => setPreviewPlan(null)}
            onSaved={loadHistory}
          />
        )}
      </div>
    </div>
  );
}
